/**
 * React Query hooks for the Discord Prospects screen. Mutations (status
 * update, scrape, rescore) invalidate the list + overview so the table and
 * KPI badges stay in sync.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DiscordProspectListParams,
  DiscordScrapeInput,
  UpdateDiscordProspectInput,
  fetchDiscordConfig,
  fetchDiscordOverview,
  fetchDiscordProspects,
  rescoreDiscordProspects,
  runDiscordScrape,
  updateDiscordProspect
} from '@/api/discordProspects';

const invalidateAll = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ['discordOverview'] });
  qc.invalidateQueries({ queryKey: ['discordProspects'] });
};

export function useDiscordOverview() {
  return useQuery({
    queryKey: ['discordOverview'],
    queryFn: fetchDiscordOverview,
    refetchInterval: 120_000
  });
}

export function useDiscordConfig() {
  return useQuery({
    queryKey: ['discordConfig'],
    queryFn: fetchDiscordConfig,
    refetchInterval: 120_000
  });
}

export function useDiscordProspects(
  params: DiscordProspectListParams,
  enabled = true
) {
  return useQuery({
    queryKey: ['discordProspects', params],
    queryFn: () => fetchDiscordProspects(params),
    enabled,
    refetchInterval: 120_000,
    placeholderData: prev => prev
  });
}

export function useUpdateDiscordProspect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; input: UpdateDiscordProspectInput }) =>
      updateDiscordProspect(vars.id, vars.input),
    onSuccess: () => invalidateAll(qc)
  });
}

export function useRunDiscordScrape() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DiscordScrapeInput) => runDiscordScrape(input),
    onSuccess: () => invalidateAll(qc)
  });
}

export function useRescoreDiscordProspects() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => rescoreDiscordProspects(),
    onSuccess: () => invalidateAll(qc)
  });
}
